const TAB_ELEMENTS_SELECTORS = [
  "a[href]",
  "area",
  "input",
  "area",
  "select",
  "textarea",
  "button",
  "iframe",
  "object",
  "[tabindex]",
  "audio[controls]",
  "video[controls]",
  "[contenteditable]:not([contenteditable='false'])",
];

/**
 * Returns if given keyevent was by tab key
 *
 * @param {Event} e
 * @returns {boolean} isHidden
 */
const isTabKeyEvent = (e: { key: string; keyCode: number }) => e.key === "Tab" || e.keyCode === 9;

/**
 * Returns if given tab element is hidden
 *
 * @param {HTMLElement} elem
 * @returns {boolean} isHidden
 */
const isElementHidden = (elem: HTMLElement | Element) => {
  return (
    (elem as HTMLElement).offsetWidth === null ||
    (elem as HTMLElement).offsetHeight === null ||
    getComputedStyle(elem as Element).display === "none" ||
    getComputedStyle(elem as Element).visibility === "hidden"
  );
};

/**
 * Returns tabindex for given element
 * Returns 0 if no tabindex attribute is
 *
 * @param {HTMLElement} elem
 * @returns {number} tabindex
 */
const getElementTabIndex = (elem: HTMLElement | Element): number => {
  let tabIndex = parseInt(elem.getAttribute("tabIndex") || "", 10);
  tabIndex = !isNaN(tabIndex) ? tabIndex : 0;
  return tabIndex;
};

/**
 * Helper to get all focusable elements within a container element
 * Further filtering for tabindex, disabled state and visibilty
 *
 * @param {HTMLElement} containerElem
 * @returns {Array} tabElements
 */
const getTabElements = (containerElem: HTMLElement | Element) => {
  const tabElements = containerElem.querySelectorAll(TAB_ELEMENTS_SELECTORS.join(","));
  return Array.from(tabElements).filter(elem => {
    return !(elem as HTMLInputElement).disabled && getElementTabIndex(elem) !== -1 && !isElementHidden(elem);
  });
};

/**
 * Set focus on child element within container
 * if no target element is defined the first tabbable child is focused
 * if force parameter is set
 *
 * @param {HTMLElement} containerElem
 * @param {HTMLElement} targetElem optional, first tabbable child gets focus
 * @param {boolean} force try to set focus no matter if element is considered as tabbable
 * @returns {boolean}
 */
export const focusUponElement = (
  containerElem: HTMLElement,
  targetElem?: HTMLElement | Element,
  force = false,
): boolean => {
  if (!containerElem) {
    console.warn("Container element which should set focus upon child doesnt not exist");
    return false;
  }
  if (targetElem && !containerElem.contains(targetElem as Node)) {
    console.warn("Child element which should get focus is not contained");
    return false;
  }

  const tabElements = getTabElements(containerElem);

  if (targetElem && force) {
    if (!targetElem.getAttribute("tabindex")) {
      targetElem.setAttribute("tabindex", "-1");
    }
    (targetElem as HTMLElement).focus();
  } else if (!targetElem && tabElements.length > 0) {
    (tabElements[0] as HTMLElement).focus();
  } else if (targetElem && tabElements.includes(targetElem as Element)) {
    (targetElem as HTMLElement).focus();
  }
  return Boolean(targetElem && document.activeElement === targetElem);
};

/**
 * Traps focus within a container element
 * Checks for tab key events and loops the focus elements within tabbable elements
 *
 * @param {KeyboardEvent} e
 * @param {HTMLElement} containerElem
 */
export const focusTrap = (e: KeyboardEvent, containerElem: HTMLElement) => {
  if (!isTabKeyEvent(e)) return;
  if (!containerElem) {
    console.warn("Element which should be focus-trapped does not exist");
  }

  const tabElements = getTabElements(containerElem) as HTMLElement[];
  if (tabElements.length > 1) {
    const targetEl = e.target as HTMLElement;
    const firstTabElement = tabElements[0];
    const lastTabElement = tabElements[tabElements.length - 1];

    // keyboard event is triggered on one of the non-tabElements (e.g. container)
    // so we focus last if shiftKey depressed
    // (first - or next tabElement will be focused with default action)
    if (!tabElements.includes(targetEl) && containerElem.contains(targetEl)) {
      if (e.shiftKey) {
        lastTabElement.focus();
        e.preventDefault();
      }
    } else if (e.shiftKey && targetEl === firstTabElement) {
      lastTabElement.focus();
      e.preventDefault();
    } else if (!e.shiftKey && targetEl === lastTabElement) {
      firstTabElement.focus();
      e.preventDefault();
    }
  }
};
